import {Button, Modal} from "antd";
import {Differ, Viewer} from "json-diff-kit";
import "json-diff-kit/dist/viewer.css";
import React from "react";
import * as styles from "./JsonDifferenceModal.module.css";

const differ = new Differ({
    detectCircular: true,
    maxDepth: Infinity,
    showModifications: true,
    arrayDiffMethod: "lcs",
});

const diffViewerProps = {
    indent: 4,
    highlightInlineDiff: true,
    hideUnchangedLines: true,
};

type CommonProps = {
    open: boolean;
    title: React.ReactNode;
    width?: number;
    onClose: () => void;
    afterOpenChange?: (open: boolean) => void;
};

type ValueProps = CommonProps & {
    mode: "value";
    value: unknown;
    valueTitle?: React.ReactNode;
};

type DifferenceProps = CommonProps & {
    mode?: "difference";
    oldValue: unknown;
    newValue: unknown;
    oldValuePresent?: boolean;
    newValuePresent?: boolean;
    oldTitle?: React.ReactNode;
    newTitle?: React.ReactNode;
};

type Props = ValueProps | DifferenceProps;

function JsonDifferenceModal(props: Props) {
    const difference = useDifference(props);

    return (
        <Modal
            open={props.open}
            title={props.title}
            width={props.width ?? 1200}
            footer={<Button type="primary" onClick={props.onClose}>Close</Button>}
            onCancel={props.onClose}
            mask={{closable: false}}
            afterOpenChange={props.afterOpenChange}
            style={{top: 24}}
        >
            <div className={styles.content}>
                {props.mode === "value"
                    ? renderValueContent(props)
                    : renderDifferenceContent(props, difference)}
            </div>
        </Modal>
    );
}

function renderValueContent(props: ValueProps) {
    return (
        <>
            {props.valueTitle && <div className={styles.valueTitle}>{props.valueTitle}</div>}
            {renderSingleValue(props.value, styles.singleValue)}
        </>
    );
}

function renderDifferenceContent(props: DifferenceProps, difference: ReturnType<typeof differ.diff> | undefined) {
    const hasOldValue = props.oldValuePresent ?? hasValue(props.oldValue);
    const hasNewValue = props.newValuePresent ?? hasValue(props.newValue);
    const oldTitle = props.oldTitle ?? "Original value";
    const newTitle = props.newTitle ?? "New value";

    if (hasOldValue && hasNewValue) {
        return (
            <>
                <div className={styles.header}>
                    <span>{oldTitle}</span>
                    <span>{newTitle}</span>
                </div>
                <Viewer
                    className={styles.diff}
                    diff={difference}
                    {...diffViewerProps}
                />
            </>
        );
    }

    return (
        <div className={styles.values}>
            <div className={styles.value}>
                <div className={styles.valueTitle}>{oldTitle}</div>
                {hasOldValue
                    ? renderSingleValue(props.oldValue, `${styles.fullValue} ${styles.removedValue}`)
                    : <i className={styles.emptyValue}>No value</i>}
            </div>
            <div className={styles.value}>
                <div className={styles.valueTitle}>{newTitle}</div>
                {hasNewValue
                    ? renderSingleValue(props.newValue, `${styles.fullValue} ${styles.addedValue}`)
                    : <i className={styles.emptyValue}>No value</i>}
            </div>
        </div>
    );
}

function useDifference(props: Props) {
    const cachedDifference = React.useRef<ReturnType<typeof differ.diff>>();

    const hasOldValue = props.mode !== "value" && (props.oldValuePresent ?? hasValue(props.oldValue));
    const hasNewValue = props.mode !== "value" && (props.newValuePresent ?? hasValue(props.newValue));
    const shouldCalculate = props.mode !== "value" && props.open && hasOldValue && hasNewValue;

    return React.useMemo(() => {
        if (props.mode === "value" || (props.open && (!hasOldValue || !hasNewValue))) {
            cachedDifference.current = undefined;

            return undefined;
        }

        if (!shouldCalculate) {
            return cachedDifference.current;
        }

        cachedDifference.current = differ.diff(props.oldValue, props.newValue);

        return cachedDifference.current;
    }, [hasOldValue, hasNewValue, props.mode, props.newValue, props.oldValue, props.open, shouldCalculate]);
}

function renderSingleValue(value: unknown, className: string) {
    return <pre className={className}>{JSON.stringify(value, null, 4)}</pre>;
}

function hasValue(value: unknown) {
    return value !== null && value !== undefined;
}

export default JsonDifferenceModal;
